import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Sliders, RefreshCw, RotateCcw, Save } from "lucide-react";
import { Navigate } from "react-router-dom";

export default function KpiWeightsPage() {
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recompute, setRecompute] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kpi/catalog");
      const onlyCats = data.map(({ metrics, ...c }) => c);
      setCats(onlyCats);
      setOriginal(onlyCats);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const accessAllowed = ["CEO", "Project Manager"].includes(user.access_role);
  const total = useMemo(() => cats.reduce((s, c) => s + Number(c.weight || 0), 0), [cats]);
  const dirty = useMemo(
    () => cats.some((c, i) => Number(c.weight) !== Number(original[i]?.weight ?? c.weight)),
    [cats, original]
  );

  if (!accessAllowed) return <Navigate to="/dashboard" replace />;

  const setWeight = (idx, v) => {
    const n = Math.max(0, Math.min(100, parseInt(v, 10) || 0));
    setCats((arr) => arr.map((c, i) => (i === idx ? { ...c, weight: n } : c)));
  };
  const distributeEvenly = () => {
    const n = cats.length;
    if (!n) return;
    const base = Math.floor(100 / n);
    const remainder = 100 - base * n;
    setCats((arr) => arr.map((c, i) => ({ ...c, weight: base + (i < remainder ? 1 : 0) })));
  };
  const reset = () => setCats(original);
  const save = async () => {
    if (total !== 100) {
      toast.error(`Weights must total 100 (currently ${total})`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/kpi/weights", {
        weights: cats.map((c) => ({ category_id: c.id, weight: Number(c.weight) })),
        recompute,
      });
      toast.success(
        recompute && data.summaries_recomputed
          ? `Saved · ${data.summaries_recomputed} historical summaries recomputed`
          : "Weights saved"
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save weights");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">Configuration</p>
          <h1 className="text-h1 mt-1">KPI Category Weights</h1>
          <p className="text-sm text-slate-500 mt-1">Rebalance how each category contributes to the overall weighted score. Must total 100.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={distributeEvenly} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-slate-200 hover:border-sky-400 text-slate-700 hover:text-sky-800 text-sm font-semibold bg-white" data-testid="kpi-even-btn">
            <Sliders size={14} /> Distribute evenly
          </button>
          <button onClick={reset} disabled={!dirty} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-slate-200 hover:border-slate-300 text-slate-600 text-sm font-semibold bg-white disabled:opacity-50" data-testid="kpi-reset-btn">
            <RotateCcw size={14} /> Reset
          </button>
          <button onClick={save} disabled={saving || !dirty || total !== 100}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold shadow-sm disabled:opacity-50" data-testid="kpi-save-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save weights"}
          </button>
        </div>
      </div>

      <div className="nv-card p-5 space-y-4" data-testid="kpi-weights-card">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <>
            {cats.map((c, i) => (
              <div key={c.id} className="flex items-center gap-4" data-testid={`kpi-row-${c.category_key}`}>
                <div className="w-44 shrink-0">
                  <p className="text-sm font-semibold text-slate-800">{c.category_name}</p>
                  <p className="text-[12.5px] uppercase tracking-wider font-semibold text-slate-400">{c.category_key}</p>
                </div>
                <input
                  type="range" min={0} max={100} step={1}
                  value={c.weight} onChange={(e) => setWeight(i, e.target.value)}
                  className="flex-1 accent-sky-700"
                  data-testid={`kpi-slider-${c.category_key}`}
                />
                <input
                  type="number" min={0} max={100} value={c.weight}
                  onChange={(e) => setWeight(i, e.target.value)}
                  className="w-20 px-2 py-1 rounded-md border border-slate-200 text-sm text-right tabular-nums"
                  data-testid={`kpi-input-${c.category_key}`}
                />
                <span className="w-10 text-sm font-semibold text-slate-500">%</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={recompute} onChange={(e) => setRecompute(e.target.checked)} className="accent-sky-700" data-testid="kpi-recompute-toggle" />
                Recompute existing monthly summaries with the new weights
              </label>
              <p className={`text-sm font-semibold ${total === 100 ? "text-emerald-700" : "text-rose-600"}`} data-testid="kpi-total">
                Total: {total} / 100 {total === 100 ? "✓" : (total < 100 ? `(missing ${100 - total})` : `(over by ${total - 100})`)}
              </p>
            </div>
          </>
        )}
      </div>

      <div className="text-xs text-slate-500 leading-relaxed">
        <p><b>Tip:</b> Saving with recompute enabled re-aggregates every existing monthly summary so the dashboard, leaderboard, and printable reports reflect the new weights instantly.</p>
      </div>
    </div>
  );
}
